/**
 * Search in the Wiki (`project/:p/wiki`): every page of the project, and every
 * typed page of a type placed in its tree, as one table, with filters and the search text in the URL
 * (`?q=` and `?filters=`), so a link opens it already filtered. Page text
 * comes from the team's search index through the docs session.
 */
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCollabDocsUI, useCollabPagesState } from '@nimbalyst/collab-bundle/docs-ui';
import { ProjectScopeSummary, ProjectSwitcher, useDocsShell } from '../collab/CollabDocsShell';
import { CollabLoadingPanel } from '../components/Chrome';
import { orgDocumentPath, orgPageItemPath, orgWikiPath } from '../routing';
import { useOptionalProjectTrackerSession } from '../trackers/ProjectTrackerSession';
import { usePagesTrackers } from './PagesTrackers';
import { usePageModule } from './pageModule';

export function WebPagesSearchRoute() {
  const { team, project, projectId, navigate, onSelectProject } = useDocsShell();
  const routerNavigate = useNavigate();
  const location = useLocation();
  const { session } = useCollabDocsUI();
  const { documents, typePlacements } = useCollabPagesState();
  const { available } = usePagesTrackers();
  const projectTrackers = useOptionalProjectTrackerSession();
  const module = usePageModule();
  const me = projectTrackers?.identity?.email ?? null;

  const { memberEmail, authorLabel } = useMemo(() => {
    const members = projectTrackers?.members ?? [];
    const byId = new Map(members.map((member) => [String(member.memberId), member]));
    const byEmail = new Map(members.flatMap((member) => (member.email ? [[member.email.toLowerCase(), member] as const] : [])));
    return {
      memberEmail: (memberId: string) => byId.get(memberId)?.email ?? null,
      authorLabel: (author: string) => {
        if (me && author.toLowerCase() === me.toLowerCase()) return 'You';
        const member = byEmail.get(author.toLowerCase()) ?? byId.get(author);
        return member?.name || member?.email || (author.includes('@') && author.split('@')[0]) || 'Unknown';
      },
    };
  }, [projectTrackers?.members, me]);
  const searchPages = useMemo(() => session.searchPages.bind(session), [session]);

  if (!available || !module) return <CollabLoadingPanel title="Opening Search" detail="Connecting to your team's pages." />;
  const { PagesSearchView } = module;
  return (
    <PagesSearchView
      lane="team"
      title={(
        <span className="flex min-w-0 flex-col">
          <ProjectSwitcher team={team} project={project} projects={team.projects} onSelectProject={onSelectProject} />
          <span className="truncate text-[11px] text-nim-faint"><ProjectScopeSummary team={team} /></span>
        </span>
      )}
      pages={documents}
      typePlacements={typePlacements}
      memberEmail={memberEmail}
      authorLabel={authorLabel}
      me={me}
      search={location.search.replace(/^\?/, '')}
      onSearchChange={(search, { replace }) => routerNavigate(orgWikiPath(team, projectId, search), { replace })}
      searchPages={searchPages}
      onOpenPage={(documentId) => navigate(orgDocumentPath(team, projectId, documentId))}
      onOpenItem={(itemId) => navigate(orgPageItemPath(team, projectId, itemId))}
    />
  );
}
